import { apiClient } from ".";
import { useQuery } from "@tanstack/react-query";
import type { CustomerCredentials } from "../ui/customerDataStore";
import type { PensionPolicy, TeaserData } from "./useGetInsurelyFees";
import type { IdentityVerificationId } from "../../types";
import { setStatsigUserId } from "../../utils/statsig";

// Pre-computed teaser that backend returns inline with auth when a calculation already exists for the lead. Lets the
// result page render without re-calling the fees endpoint.
// capital and policies are absent on calculations cached before they were added.
export interface CachedCalculation extends TeaserData {
  // Summed value of the policies behind the displayed teaser.
  capital?: number;
  policies?: PensionPolicy[];
  calculatedAt: string;
}

// Opt-in marketing channels stored as the ISO timestamp of the opt-in (present = opted in).
export interface MarketingPreferences {
  futureOffers?: string;
  newsletter?: string;
}

export interface AppAuthResponse {
  credentials: CustomerCredentials;
  isCustomer: boolean;
  customerUuid?: string | null;
  leadId?: string;
  // The identity behind the app's BankID session. app-auth refuses one without it.
  firstName: string;
  lastName: string;
  personalNumber: string;
  // Proof that CAS verified this person. /user/user refuses a signup without it,
  // and the POA is stored after that call. Null when CAS omitted the claim.
  identityVerificationId: IdentityVerificationId;
  marketingPreferences?: MarketingPreferences | null;
  appData?: {
    cachedCalculation?: CachedCalculation;
    engagement?: {
      ctaClickedAt?: string;
      insurelyInitiatedAt?: string;
    };
  };
}

export const fetchAppAuth = async (
  flowId?: string,
  poa = false,
): Promise<AppAuthResponse> => {
  const params = {
    ...(flowId ? { flowId } : {}),
    ...(poa ? { poa: "true" } : {}),
  };

  const response = await apiClient({
    url: `/auth/app-auth`,
    method: "GET",
    // The handoff normally arrives as the encrypted_flow_id cookie, which the browser
    // sends to the API host on its own. Released app builds still send a raw flowId.
    // `poa` tells auth to skip the lead: that journey creates a real user instead.
    ...(Object.keys(params).length ? { params } : {}),
  });

  // Awaited so the POA gate is read for this customer, not the anonymous visitor.
  await setStatsigUserId(response.data?.customerUuid);

  return response.data;
};

// `enabled` is the caller's answer to "did the app open this page", and must also stay
// false until the visitor's POA gate has loaded: `poa` changes what the backend writes.
export const useAppAuth = (
  flowId: string | undefined,
  enabled = true,
  poa = false,
) =>
  useQuery({
    queryKey: ["appAuth", flowId, poa],
    queryFn: () => fetchAppAuth(flowId, poa),
    enabled,
    retry: 3,
  });
